// SPA ↔ browser-extension pairing bridge. The extension pins its ID via a manifest "key",
// so EXT_ID is stable and known here. We push the current session token to the extension,
// which exchanges it for its own device-scoped session (see /auth/sessions/exchange). The
// token only ever travels to that one pinned extension ID over chrome.runtime messaging.
import { getSessionToken } from "../api/client";

/** Stable extension id (derived from deploy/ext-signing.key; mirrors web/extension/ext-id.txt). */
export const EXT_ID = "mladdiaikljfegejpeebppefhlidolfa";

const PAIRED_FLAG = "vault.ext.paired";

interface Runtime {
  sendMessage: (id: string, msg: unknown, cb?: (resp: unknown) => void) => void;
  lastError?: unknown;
}

function runtime(): Runtime | null {
  const c = (globalThis as { chrome?: { runtime?: Runtime } }).chrome;
  return c?.runtime && typeof c.runtime.sendMessage === "function" ? c.runtime : null;
}

/** True when a Chromium extension runtime is reachable (Chrome/Edge with our ext installed). */
export function extensionAvailable(): boolean {
  return runtime() !== null;
}

/** True on a Chromium browser (Chrome/Edge) — where the extension can be installed at all. Used
 *  to surface the setup guide even before the extension is present. Excludes Firefox/Safari. */
export function isChromium(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /Chrome\//.test(ua) && !/OPR\//.test(ua);
}

function send(msg: unknown): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const rt = runtime();
    if (!rt) return reject(new Error("no-extension"));
    try {
      rt.sendMessage(EXT_ID, msg, (resp) => {
        const err = (globalThis as { chrome?: { runtime?: { lastError?: { message?: string } } } }).chrome?.runtime?.lastError;
        if (err) reject(new Error(err.message ?? "extension-error"));
        else resolve(resp);
      });
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

/** Push the current session token to the extension so it can pair. Marks paired on success. */
export async function pairExtension(login: string): Promise<void> {
  const token = getSessionToken();
  if (!token) throw new Error("no-session");
  const resp = (await send({ type: "vault-ext-pair", token, login })) as { ok?: boolean; error?: string } | undefined;
  if (!resp?.ok) throw new Error(resp?.error ?? "pair-failed");
  try {
    localStorage.setItem(PAIRED_FLAG, "1");
  } catch {
    /* ignore */
  }
}

/** If the user paired before, silently re-push the token on SPA load (12h sessions expire
 *  daily; this re-pairs SSO users without a click). Best-effort and non-blocking. */
export async function maybeRepairExtension(login: string): Promise<void> {
  let paired = false;
  try {
    paired = localStorage.getItem(PAIRED_FLAG) === "1";
  } catch {
    /* ignore */
  }
  if (!paired || !extensionAvailable() || !getSessionToken()) return;
  try {
    const ping = (await send({ type: "vault-ext-ping" })) as { ok?: boolean; needsToken?: boolean } | undefined;
    if (ping?.needsToken) await pairExtension(login);
  } catch {
    /* extension gone or locked — ignore, user can re-pair from Settings */
  }
}
